import React, { useEffect, useState } from "react";
import { IonIcon } from "@ionic/react";
import { useTranslation } from "react-i18next";
import { telegramIcon, telegramLogoRound } from "@core/icons";
import useGetTGVerificationToken from "../../../../providers/telegram-auth/useGetTGVerificationToken";
import useAuthByTelegram from "../../../../providers/telegram-auth/useAuthByTelegram";
import { useHandleCustomerLogin } from "../../../../hooks/useHandleCustomerLogin";
import { CustomBottomSheet } from "../../../../components/UI/CustomBottomSheet";
import useConfig from "../../../../providers/config/useConfig";
import { useHistory } from "react-router";

interface TelegramAuthModalProps {
  isOpen: boolean;
  onDismiss: () => void;
}

const TelegramAuthModal: React.FC<TelegramAuthModalProps> = ({ isOpen, onDismiss }) => {
  const { telegramAuth: telegramAuthConfig } = useConfig();
  const history = useHistory<{ from?: string }>();
  const { t } = useTranslation();
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [hasRedirected, setHasRedirected] = useState(false);
  const handleCustomerLogin = useHandleCustomerLogin();
  const getTokenMutation = useGetTGVerificationToken({
    onSuccess: (data) => {
      setToken(data.token);
    },
  });

  const { data: authData } = useAuthByTelegram(token!, {
    enabled: !!token && hasRedirected && isOpen,
    refetchInterval: 3000,
    refetchOnWindowFocus: true,
    refetchIntervalInBackground: false,
  });

  const handleClose = () => {
    setToken(null);
    setHasRedirected(false);
    onDismiss();
  };

  useEffect(() => {
    const handleAuth = async () => {
      if (authData && !("customer_confirmed" in authData) && isOpen) {
        setIsLoading(true);
        await handleCustomerLogin(authData);

        if (history.location.state?.from) {
          history.goBack();
        } else {
          history.replace("/home3");
        }

        setIsLoading(false);
        handleClose();
      }
    };

    handleAuth();
  }, [authData]);

  useEffect(() => {
    if (isOpen) {
      getTokenMutation.mutate();
    }
  }, [isOpen]);

  const handleOpenTelegram = () => {
    if (!token || !telegramAuthConfig?.enabled) return;
    const telegramDeepLink = `tg://resolve?domain=${telegramAuthConfig?.botUsername}&start=${token}`;

    // Try to open Telegram app
    window.location.href = telegramDeepLink;
    setHasRedirected(true);
  };

  useEffect(() => {
    if (token && isOpen) {
      handleOpenTelegram();
    }
  }, [token, isOpen]);

  return (
    <CustomBottomSheet
      open={isOpen}
      onDismiss={handleClose}
      onBack={handleClose}
      title={t("Login with Telegram")}
      showDefaultHeader
      showDefaultPreview={false}
      contentClassName="px-4 pb-6"
    >
      <div className="flex flex-col items-center justify-center px-2 py-4 h-[90vh]">
        {isLoading ? (
          <div className="flex flex-col items-center gap-4">
            <div className="w-6 h-6 border-2 border-gray-300 border-t-transparent rounded-full animate-spin" />
            <span className="text-sm text-gray-700">{t("Loading...")}</span>
          </div>
        ) : (
          <>
            <div className="mb-8 text-center">
              <div className="flex justify-center mb-4">
                  <IonIcon icon={telegramLogoRound} style={{ fontSize: "84px" }} />
              </div>
              <h2 className="mt-4 text-xl font-semibold">{t("Login with Telegram")}</h2>
              <p className="mt-2 text-gray-600">
                {t("Click the button below to open Telegram and complete authentication")}
              </p>
              <p className="mt-2 text-gray-600">
                {t("In Telegram, press Start and share your phone number with the bot.")}
              </p>
              {hasRedirected && (
                <p className="mt-2 text-xs text-gray-500">
                  {t("Waiting for confirmation... Return to the app after sharing your number.")}
                </p>
              )}
            </div>
            <button
              onClick={handleOpenTelegram}
              disabled={!token}
              className="w-full bg-[#0088cc] text-white rounded-full py-3 px-4 flex items-center justify-center gap-2 disabled:opacity-50"
            >
              <IonIcon icon={telegramIcon} />
              <span>{t("Open Telegram")}</span>
            </button>
          </>
        )}
      </div>
    </CustomBottomSheet>
  );
};

export { TelegramAuthModal };
